import Decimal from 'decimal.js'

/**
 * Converts a given value to a number.
 *
 * @param {number | string | null | undefined} value - The value to convert.
 *        It can be a number, string, null, or undefined.
 * @returns {number} The converted number. Returns:
 *          - `0` if the value is `null` or `undefined`.
 *          - The numeric representation if the value is a valid number or numeric string.
 *          - `NaN` for non-numeric strings.
 *
 * @example
 * toNumber(42);           // 42
 * toNumber('3.14');       // 3.14
 * toNumber(null);         // 0
 * toNumber(undefined);    // 0
 * toNumber('123abc');     // NaN
 * toNumber('');           // 0
 */
export const toNumber = (value: number | string | null | undefined): number => {
    if (value === undefined) {
        return 0
    }

    if (value === null) {
        return 0
    }

    return Number(value)
}

/**
 * Same as toNumber but returns a Decimal instance.
 * @param value
 */
export const toDecimalDefaultZero = (
    value: number | string | null | Decimal | undefined,
): Decimal => {
    if (value === undefined || value === null || value === '') {
        return Decimal(0)
    }

    try {
        return Decimal(value)
    } catch {
        return Decimal(NaN)
    }
}

/**
 * parseToDecimal is intended to replace `parseFloat` and `parseInt` usages
 * @param value
 */
export const parseToDecimal = (
    value: number | string | null | Decimal | undefined,
): Decimal => {
    // Do not parse anything that `parseFloat` or `parseInt` would not.
    if (value === undefined || value === null || value === '') {
        return Decimal(NaN)
    }

    try {
        return Decimal(`${value}`.trim())
    } catch {
        return Decimal(NaN)
    }
}
